import * as React from 'react';
import { ReactComponent as CloseIcon } from '../../icons/CloseIcon.svg';
import {
  NodeFrame,
  NodeTitleBox,
  NodeTitle,
  NodeCollapseArea,
  NodeDescriptionBox,
  NodeDescription,
} from './NodeComponents';
import { ReactComponent as ExpandLessIcon } from '../../icons/ExpandLessIcon.svg';
import { ReactComponent as ExpandMoreIcon } from '../../icons/ExpandMoreIcon.svg';
import { TinyTextInput } from '../../InputControls/TinyTextInput';
import { useState } from 'react';
import { useTheme, Theme, alpha, makeStyles, createStyles } from '@material-ui/core/styles';
const useIconStyles = makeStyles(() =>
  createStyles({
    whiteIcon: {
      color: '#fff',
    },
  })
);

const getStyle = (theme: Theme) => ({
  nodeFrame: {
    background: theme.palette.background.paper,
  },
  nodeFrameConatiner: {
    position: 'absolute' as const,
    top: '-24px',
    right: '-24px',
    width: 24,
    height: 24,
    cursor: 'pointer' as const,
  },
  closeIcon: {
    color: theme.palette.text.disabled,
  },
  tinytextInput: {
    fontSize: 14,
    width: '100%',
  },
  tinyTextFont: {
    fontSize: 12,
    fontWeight: 400,
    width: '100%',
    paddingLeft: 13,
  },
});
interface TransformNodeWidgetProps {
  title: string;
  onTitleChange?: (newTitle: string) => void;
  onDescriptionChange?: (newTitle: string) => void;
  onConfigureClicked?: () => void;
  onOkConfigClicked?: () => void;
  onCancelConfigClicked?: () => void;
  onRemoveClicked?: () => void;
  onCollapseClicked?: () => void;

  description?: string;
  configMode?: boolean;
  isConfigurable?: boolean;
  isRemovable?: boolean;
  isTitleEditable?: boolean;
  isSelected?: boolean;
  isCollapsible?: boolean;
  isCollapsed?: boolean;
  children?: React.ReactNode;
  categoryColor?: string;
}

export const TransformNodeWidget: React.FunctionComponent<TransformNodeWidgetProps> = (props) => {
  const {
    title,
    description,
    onTitleChange,
    onDescriptionChange,
    onRemoveClicked,
    onCollapseClicked,
    isRemovable,
    isSelected,
    isTitleEditable,
    isCollapsible,
    isCollapsed,
    children,
    onOkConfigClicked,
    categoryColor,
  } = props;
  const [titleEditMode, setTitleEditMode] = useState(false);
  const [blur, setBlur] = useState(false);
  const theme = useTheme();
  const styles = getStyle(theme);
  const iconClasses = useIconStyles();
  const placeholderColor = theme.palette.text.disabled;
  const tinyInputOverride = (
    <style>{`
      input.tiny-text-input-dark {
        border-width: 1px !important;
        border-style: solid !important;
        border-color: transparent !important;
        outline: none !important;
        transition: border 0.2s;
      }
      input.tiny-text-input-dark:not([class*="Mui"]):not(:focus),
      [data-theme] input.tiny-text-input-dark:not([class*="Mui"]):not(:focus) {
        border-color: transparent !important;
      }
      input.tiny-text-input-dark:not([class*="Mui"]),
      textarea.tiny-text-input-dark:not([class*="Mui"]) {
        border-color: transparent !important;
      }
      input.tiny-text-input-dark:focus {
        border-width: 1px !important;
        border-style: solid !important;
        border-color: ${alpha(theme.palette.divider, 0.1)} !important;
        outline: none !important;
      }
      /* fallback for non-input elements, only in dark mode */
      [data-theme="dark"] .tiny-text-input-dark {
        border-width: 1px !important;
        border-style: solid !important;
        border-color: transparent !important;
        outline: none !important;
        transition: border 0.2s;
      }
      [data-theme="dark"] .tiny-text-input-dark:not(:focus) {
        border-color: transparent !important;
      }
      [data-theme="dark"] .tiny-text-input-dark:focus {
        border-width: 1px !important;
        border-style: solid !important;
        border-color: ${alpha(theme.palette.divider, 0.1)} !important;
        outline: none !important;
      }
        [data-theme="dark"] textarea.tiny-text-input-dark {
          background-color: transparent !important;
          border-width: 1px !important;
          border-style: solid !important;
          border-color: transparent !important;
          outline: none !important;
          transition: border 0.2s;
        }
        [data-theme="dark"] textarea.tiny-text-input-dark:not(:focus) {
          border-color: transparent !important;
        }
        textarea.tiny-text-input-dark:focus {
          border-width: 1px !important;
          border-style: solid !important;
          border-color: ${alpha(theme.palette.divider, 0.1)} !important;
          outline: none !important;
        }
    `}</style>
  );
  const styleObject = { ...styles.tinytextInput, color: theme.palette.text.primary };
  const descStyle = { ...styles.tinyTextFont, color: theme.palette.text.secondary };

  return (
    <>
      {tinyInputOverride}
      <NodeFrame
        isSelected={isSelected}
        style={{
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        }}
      >
        {isSelected && isRemovable && (
          <div onClick={onRemoveClicked} style={styles.nodeFrameConatiner}>
            <CloseIcon fill={theme.palette.text.disabled} style={styles.closeIcon} />
          </div>
        )}
        <NodeTitleBox style={{ backgroundColor: categoryColor }}>
          {isCollapsible && (
            <NodeCollapseArea onClick={onCollapseClicked}>
              {isCollapsed ? (
                <ExpandMoreIcon className={iconClasses.whiteIcon} />
              ) : (
                <ExpandLessIcon className={iconClasses.whiteIcon} />
              )}
            </NodeCollapseArea>
          )}
          {!titleEditMode && (
            <NodeTitle
              onDoubleClick={() => setTitleEditMode(true)}
              color={theme.palette.common.white}
              style={{ textShadow: '0 2px 8px rgba(0,0,0,0.75)' }}
            >
              {title}
            </NodeTitle>
          )}
          {titleEditMode && isTitleEditable && (
            <NodeTitle isTitle={titleEditMode} color={theme.palette.text.primary}>
              <TinyTextInput
                value={title}
                autoFocus
                onChange={(val) => onTitleChange && onTitleChange(val as string)}
                style={styleObject}
                onBlur={() => {
                  setTitleEditMode(false);
                  onOkConfigClicked && onOkConfigClicked();
                }}
                className="tiny-text-input-dark"
              />
            </NodeTitle>
          )}
        </NodeTitleBox>
        {!isCollapsed && (
          <NodeDescriptionBox>
            {isTitleEditable && (
              <NodeDescription
                isDesc={blur}
                onDoubleClick={() => setBlur(true)}
                color={categoryColor}
              >
                <TinyTextInput
                  isDescription
                  value={description}
                  onChange={(val) => onDescriptionChange && onDescriptionChange(val as string)}
                  style={descStyle}
                  placeholder="No description to display"
                  placeholderColor={placeholderColor}
                  onBlur={() => {
                    setBlur(false);
                    onOkConfigClicked && onOkConfigClicked();
                  }}
                  isDescriptionFocus
                  className="tiny-text-input-dark"
                />
              </NodeDescription>
            )}
          </NodeDescriptionBox>
        )}
        {children}
      </NodeFrame>
    </>
  );
};
